import DSSelect from "views/system-design/DSSelect";
import { SelectChangeEvent } from "@mui/material/Select";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSCheckbox from "views/system-design/DSCheckBox";
import DSListItemText from "views/system-design/DSListItemText";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";

export function FilterField({
  label,
  children,
}: {
  // Omitted when the caller labels the control itself — an empty caption still
  // takes a row, which drops the control below the fields beside it.
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {label && (
        <DSTypography
          variant="caption"
          sx={{ fontWeight: 600, color: "text.secondary" }}
        >
          {label}
        </DSTypography>
      )}
      {children}
    </DSBox>
  );
}

export default function MultiSelectFilter<T extends string>({
  label,
  selected,
  options,
  allLabel,
  onChange,
  disabled = false,
}: {
  label?: string;
  selected: T[];
  options: { value: T; label: string }[];
  allLabel: string;
  onChange: (values: T[]) => void;
  disabled?: boolean;
}) {
  return (
    <FilterField label={label}>
      <DSSelect<T[]>
        multiple
        disabled={disabled}
        value={selected}
        onChange={(event: SelectChangeEvent<T[]>) => {
          const value = event.target.value;
          onChange(
            (typeof value === "string" ? value.split(",") : value) as T[],
          );
        }}
        displayEmpty
        renderValue={(values) => {
          if (values.length === 0) return allLabel;
          return values
            .map(
              (value) =>
                options.find((option) => option.value === value)?.label ??
                value,
            )
            .join(", ");
        }}
        size="small"
        sx={{ minWidth: 120 }}
      >
        {options.map((option) => (
          <DSMenuItem key={option.value} value={option.value}>
            <DSCheckbox
              size="small"
              checked={selected.includes(option.value)}
              sx={{ p: 0, mr: 1 }}
            />
            <DSListItemText primary={option.label} />
          </DSMenuItem>
        ))}
      </DSSelect>
    </FilterField>
  );
}
